// Service layer for Quotes CRUD operations
import { supabase } from '../lib/supabase';

export const quoteService = {
  // Get all quotes for a project
  async getByProjectId(projectId) {
    const { data, error } = await supabase?.from('quotes')?.select(`
        *,
        vendors(*)
      `)?.eq('project_id', projectId)?.order('submitted_at', { ascending: false });

    if (error) throw error;

    return data?.map(quote => {
      const rawLineItems = quote?.line_items || {};

      // Separate _meta and internal keys from scoreable field responses
      const { _meta, _quotesheetId, _submittedAt, ...responses } = typeof rawLineItems === 'object' && !Array.isArray(rawLineItems)
        ? rawLineItems
        : {};

      return {
        id: quote?.id,
        projectId: quote?.project_id,
        vendorId: quote?.vendor_id,
        vendorName: quote?.vendor_name,
        vendor: quote?.vendors,
        status: quote?.status,
        totalAmount: quote?.total_amount,
        // lineItems contains the flat field responses (used by scoring engine)
        // _meta keys are stripped so scoring engine only sees { fieldId: value }
        lineItems: typeof rawLineItems === 'object' && !Array.isArray(rawLineItems)
          ? responses
          : rawLineItems,
        // fieldMeta is available for category-weighted comparison
        fieldMeta: _meta || null,
        quotesheetId: _quotesheetId || null,
        notes: quote?.notes,
        validUntil: quote?.valid_until,
        submittedAt: quote?.submitted_at,
        createdAt: quote?.created_at,
        updatedAt: quote?.updated_at
      };
    });
  },

  // Create quote
  async create(quoteData) {
    const { data, error } = await supabase?.from('quotes')?.insert({
        project_id: quoteData?.projectId,
        vendor_id: quoteData?.vendorId,
        status: quoteData?.status || 'pending',
        total_amount: quoteData?.totalAmount,
        line_items: quoteData?.lineItems || [],
        notes: quoteData?.notes,
        valid_until: quoteData?.validUntil,
        submitted_at: new Date()?.toISOString()
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Submit a quote (saves to Supabase, creates notification, sends email)
  async submitQuote({ projectId, vendorId, vendorName, totalAmount, lineItems, notes, validUntil, projectOwnerId, projectOwnerEmail, projectName }) {
    // 1. Upsert the quote record with submitted status
    const { data: quote, error: quoteError } = await supabase
      ?.from('quotes')
      ?.insert({
        project_id: projectId,
        vendor_id: vendorId || null,
        vendor_name: vendorName || null,
        status: 'submitted',
        total_amount: totalAmount ? parseFloat(totalAmount) : null,
        line_items: lineItems || {},
        notes: notes || null,
        valid_until: validUntil || null,
        submitted_at: new Date()?.toISOString()
      })
      ?.select()
      ?.single();

    if (quoteError) throw quoteError;

    // 2. Create in-app notification for the project owner
    if (projectOwnerId) {
      await supabase?.from('notifications')?.insert({
        user_id: projectOwnerId,
        type: 'quote',
        title: 'New Quote Submitted',
        message: `${vendorName || 'A vendor'} submitted a quote${totalAmount ? ` for $${parseFloat(totalAmount)?.toLocaleString()}` : ''} for ${projectName || 'your project'}`,
        project_id: projectId || null,
        project_name: projectName || null,
        vendor_name: vendorName || null,
        amount: totalAmount ? parseFloat(totalAmount) : null,
        action_url: '/project-comparison',
        priority: 'high',
        is_read: false
      });
    }

    return quote;
  },

  // Update quote
  async update(quoteId, quoteData) {
    const updateData = {};
    if (quoteData?.status !== undefined) updateData.status = quoteData?.status;
    if (quoteData?.totalAmount !== undefined) updateData.total_amount = quoteData?.totalAmount;
    if (quoteData?.lineItems !== undefined) updateData.line_items = quoteData?.lineItems;
    if (quoteData?.notes !== undefined) updateData.notes = quoteData?.notes;
    if (quoteData?.validUntil !== undefined) updateData.valid_until = quoteData?.validUntil;
    if (quoteData?.status === 'submitted' && !quoteData?.submittedAt) {
      updateData.submitted_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase?.from('quotes')?.update(updateData)?.eq('id', quoteId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Accept quote
  async accept(quoteId) {
    return this.update(quoteId, { status: 'accepted' });
  },

  // Reject quote
  async reject(quoteId) {
    return this.update(quoteId, { status: 'rejected' });
  },

  // Subscribe to real-time changes
  subscribeToChanges(projectId, callback) {
    const channel = supabase?.channel(`quotes_${projectId}`)?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'quotes',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};
